import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fmtMoneyEs, fmtPctEs } from '../lib/format';
import { useAuth } from './AuthContext';
export type DisplayMode = 'money' | 'percent';

// Preference is per account, like every other user-owned record.
function keyFor(userId: string): string {
  return `dze-journal:display-mode:${userId}`;
}
function loadMode(userId: string): DisplayMode {
  if (typeof window === 'undefined' || !userId) return 'percent';
  const stored = window.localStorage.getItem(keyFor(userId));
  return stored === 'money' || stored === 'percent' ? stored : 'percent';
}
interface DisplayModeValue {
  mode: DisplayMode;
  setMode: (mode: DisplayMode) => void;
  /** The account's preferred currency. */
  currency: string;
  /** Formats a result according to the active mode. */
  formatResult: (value: {
    pct: number;
    money: number;
  }) => string;
}
const DisplayModeContext = createContext<DisplayModeValue | null>(null);
export function DisplayModeProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAuth();
  const userId = user?.id ?? '';
  const currency = user?.currency ?? 'EUR';
  const [mode, setMode] = useState<DisplayMode>(() => loadMode(userId));
  useEffect(() => {
    setMode(loadMode(userId));
  }, [userId]);
  useEffect(() => {
    if (!userId) return;
    try {
      window.localStorage.setItem(keyFor(userId), mode);
    } catch {
      /* storage failures must not break the UI */
    }
  }, [userId, mode]);
  const formatResult = useCallback(({
    pct,
    money
  }: {
    pct: number;
    money: number;
  }) => mode === 'money' ? fmtMoneyEs(money, true, currency) : fmtPctEs(pct), [mode, currency]);
  const value = useMemo(() => ({
    mode,
    setMode,
    currency,
    formatResult
  }), [mode, currency, formatResult]);
  return <DisplayModeContext.Provider value={value} data-id="element-590" data-mp="1b7x6cqluiyf5">{children}</DisplayModeContext.Provider>;
}
export function useDisplayMode(): DisplayModeValue {
  const context = useContext(DisplayModeContext);
  if (!context) throw new Error('useDisplayMode must be used within DisplayModeProvider');
  return context;
}